import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  Stack,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { getDeliveries, getDeliveryCompanies, getDrivers } from "../../api/adminService";
import { DeliveryStatusColors, DeliveryStatusLabels, formatCurrency, formatDate } from "./constants";

export default function Delivery() {
  const [tab, setTab] = useState("deliveries");

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Delivery
      </Typography>
      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab value="deliveries" label="Active Deliveries" />
        <Tab value="drivers" label="Drivers" />
      </Tabs>
      {tab === "deliveries" ? <DeliveriesTab /> : <DriversTab />}
    </Box>
  );
}

function DeliveriesTab() {
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [totalFee, setTotalFee] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [companyFilter, setCompanyFilter] = useState("");
  const [companies, setCompanies] = useState([]);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });

  useEffect(() => {
    getDeliveryCompanies()
      .then(setCompanies)
      .catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    getDeliveries({
      status: statusFilter || undefined,
      deliveryCompanyUserId: companyFilter || undefined,
      page: paginationModel.page + 1,
      pageSize: paginationModel.pageSize,
    })
      .then((data) => {
        setRows(data.items);
        setRowCount(data.totalCount);
        setTotalFee(data.totalAmountSum || 0);
        setError("");
      })
      .catch(() => setError("Could not load deliveries. Is the API running?"))
      .finally(() => setLoading(false));
  }, [statusFilter, companyFilter, paginationModel]);

  const columns = useMemo(
    () => [
      { field: "orderNumber", headerName: "Order #", width: 160 },
      { field: "storeName", headerName: "Store", flex: 1, minWidth: 150 },
      { field: "customerName", headerName: "Customer", flex: 1, minWidth: 150 },
      {
        field: "dropoffCity",
        headerName: "Dropoff",
        width: 160,
        valueGetter: (v, row) => `${row.dropoffCity}, ${row.dropoffStreet}`,
      },
      { field: "driverName", headerName: "Driver", width: 150, valueGetter: (v) => v || "Unassigned" },
      {
        field: "deliveryCompanyName",
        headerName: "Delivery Company",
        width: 170,
        valueGetter: (v) => v || "Independent",
      },
      {
        field: "status",
        headerName: "Status",
        width: 160,
        renderCell: (params) => (
          <Chip size="small" label={DeliveryStatusLabels[params.value]} color={DeliveryStatusColors[params.value]} />
        ),
      },
      {
        field: "deliveryFee",
        headerName: "Delivery Fee",
        width: 120,
        valueFormatter: (v) => formatCurrency(v),
      },
      { field: "orderCreatedAt", headerName: "Ordered", width: 170, valueFormatter: (v) => formatDate(v) },
    ],
    []
  );

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Stack spacing={1.5} sx={{ mb: 2 }}>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={statusFilter}
          onChange={(e, v) => {
            setStatusFilter(v ?? "");
            setPaginationModel((p) => ({ ...p, page: 0 }));
          }}
        >
          <ToggleButton value="">All statuses</ToggleButton>
          {Object.entries(DeliveryStatusLabels).map(([value, label]) => (
            <ToggleButton key={value} value={value}>
              {label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <ToggleButtonGroup
          size="small"
          exclusive
          value={companyFilter}
          onChange={(e, v) => {
            setCompanyFilter(v ?? "");
            setPaginationModel((p) => ({ ...p, page: 0 }));
          }}
        >
          <ToggleButton value="">All companies</ToggleButton>
          {companies.map((company) => (
            <ToggleButton key={company.userId} value={company.userId}>
              {company.companyName}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Stack>

      <Box sx={{ bgcolor: "background.paper", borderRadius: 2 }}>
        <DataGrid
          autoHeight
          rows={rows}
          getRowId={(row) => row.deliveryId}
          columns={columns}
          rowCount={rowCount}
          loading={loading}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick
          sx={{ border: "none" }}
        />
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1.5, pr: 1 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          Total Delivery Fee: {formatCurrency(totalFee)}
        </Typography>
      </Box>
    </Box>
  );
}

function DriversTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    setLoading(true);
    getDrivers({})
      .then((data) => {
        setRows(data);
        setError("");
      })
      .catch(() => setError("Could not load drivers. Is the API running?"))
      .finally(() => setLoading(false));
  }, []);

  const columns = useMemo(
    () => [
      { field: "fullName", headerName: "Name", flex: 1, minWidth: 160 },
      { field: "phone", headerName: "Phone", width: 140, valueGetter: (v) => v || "-" },
      { field: "vehicleType", headerName: "Vehicle", width: 130, valueGetter: (v) => v || "-" },
      { field: "licensePlate", headerName: "Plate", width: 110, valueGetter: (v) => v || "-" },
      {
        field: "deliveryCompanyName",
        headerName: "Company",
        width: 160,
        valueGetter: (v) => v || "Independent",
      },
      {
        field: "isAvailable",
        headerName: "Available",
        width: 120,
        renderCell: (params) => (
          <Chip
            size="small"
            label={params.value ? "Available" : "Offline"}
            color={params.value ? "success" : "default"}
          />
        ),
      },
      { field: "rating", headerName: "Rating", width: 100 },
      { field: "activeDeliveryCount", headerName: "Active", width: 100 },
      { field: "completedDeliveryCount", headerName: "Completed", width: 110 },
    ],
    []
  );

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Box sx={{ bgcolor: "background.paper", borderRadius: 2 }}>
        <DataGrid
          autoHeight
          rows={rows}
          getRowId={(row) => row.userId}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          sx={{ border: "none" }}
        />
      </Box>
    </Box>
  );
}
